import type { Registry as Services } from '@ember/service';
import type { RouterContract } from 'compatibility-router';

type Search = Record<string, string | number | boolean | null | undefined>;

/**
 * Resolves a `$`-placeholder path against the current URL. Each `$name` segment
 * is replaced by the matching key in `params`, falling back to the value at the
 * same position in the current URL (so `$hotelId` tracks the hotel in view).
 * Throws when a placeholder is satisfied by neither, so callers never receive a
 * path that still contains a `$` token.
 *
 * @example
 * // currentURL: /hotel/42/settings
 * resolvePath(router, '/hotel/$hotelId/settings/personal');
 * // => '/hotel/42/settings/personal'
 *
 * @example
 * // currentURL: /hotel/42/overview
 * resolvePath(router, '/hotel/$hotelId/$section', { section: 'settings' });
 * // => '/hotel/42/settings'
 */
function resolvePath(
  router: Services['router'],
  to: string,
  params?: Record<string, string>,
): string {
  const [current = ''] = (router.currentURL ?? '').split('?');
  const segments = current.split('/');
  const missing: string[] = [];

  const path = to
    .split('/')
    .map((segment, index) => {
      if (!segment.startsWith('$')) {
        return segment;
      }
      const key = segment.slice(1);
      const value = params?.[key] ?? segments[index];
      if (value === undefined) {
        missing.push(key);
        return segment;
      }
      return value;
    })
    .join('/');

  if (missing.length > 0) {
    throw new Error(`Missing route params: ${missing.join(',')}`);
  }
  return path;
}

function recognizeOwnRoute(router: Services['router'], path: string): boolean {
  // Filter out lumen's catch-all (`app/router.ts`) index route: this route is
  // not owned by lumen and just redirects.
  const route = router.recognize(path);
  return !!route && route.name !== 'index';
}

function appendSearch(path: string, search?: Search): string {
  if (!search) {
    return path;
  }
  const query = new URLSearchParams();
  for (const [key, value] of Object.entries(search)) {
    if (value !== null && value !== undefined) {
      query.append(key, String(value));
    }
  }
  const queryString = query.toString();
  return queryString ? `${path}?${queryString}` : path;
}

/**
 * Translates Ember's RouterService into compatibility-router's contract, the
 * layer mounted React packages route through. `recognize` reports whether a
 * path belongs to lumen, so the package can do an SPA transition for owned
 * routes and a hard navigation for anything else.
 */
export default function createCompatibilityRouter(
  router: Services['router'],
): RouterContract {
  return {
    buildHref: ({ to, params, search }): string =>
      appendSearch(resolvePath(router, to, params), search),
    recognize: (to): boolean =>
      recognizeOwnRoute(router, resolvePath(router, to)),
    navigate: ({ to, params, search, replace }): void => {
      const url = appendSearch(resolvePath(router, to, params), search);
      if (replace) {
        router.replaceWith(url);
      } else {
        router.transitionTo(url);
      }
    },
  };
}
